// ============================================================
// CharacterLibrary - Header dropdown panel for browsing, managing,
// and loading saved characters. CRUD, tag filtering, import/export,
// hover preview, recent loads, duplicate.
//
// Two views: the workshop folder (`CharacterFolderView`) and this
// session's list (`characterLibrary/LibraryListView`). The panel's
// state is `characterLibrary/useLibraryPanel`, held here so it
// survives switching between them.
//
// Storage-agnostic: receives a `library` prop conforming to
// UseCharacterLibraryReturn. In solo mode this is backed by
// localStorage; in multiplayer it's backed by Firestore session
// characters. The component doesn't know or care which backend.
// ============================================================
import { useRef } from 'react';
import common from '../styles/common.module.css';
import CharacterFolderView from './CharacterFolderView';
import styles from '../styles/CharacterLibrary.module.css';
import clsx from '../utils/clsx';
import LibraryListView, { importFromInput } from './characterLibrary/LibraryListView';
import type { CharacterLibraryProps } from './characterLibrary/libraryModel';
import FromMyLibrary from './characterLibrary/FromMyLibrary';
import { useCloseOnOutsidePointer, useLibraryPanel } from './characterLibrary/useLibraryPanel';
import type { LibraryPanel } from './characterLibrary/useLibraryPanel';

function ViewTabs({ panel }: { panel: LibraryPanel }) {
  const { view, setView } = panel;
  return (
    <div className={styles.viewTabs} role="tablist" aria-label="Character source">
      <button
        role="tab"
        aria-selected={view === 'folder'}
        className={clsx(common.btnSmall, view === 'folder' && styles.viewTabActive)}
        data-testid="library-view-folder"
        onClick={() => setView('folder')}
      >
        Folder
      </button>
      <button
        role="tab"
        aria-selected={view === 'library'}
        className={clsx(common.btnSmall, view === 'library' && styles.viewTabActive)}
        data-testid="library-view-list"
        onClick={() => setView('library')}
      >
        This session
      </button>
    </div>
  );
}

interface FolderProps {
  props: CharacterLibraryProps;
  panel: LibraryPanel;
  onImportFile: () => void;
}

function FolderTab({ props, panel, onImportFile }: FolderProps) {
  const { folder } = panel;
  return (
    <CharacterFolderView
      selected={folder.picked}
      onToggleSelect={folder.toggle}
      onSelectGroup={folder.selectGroup}
      onOrder={folder.reportOrder}
      onClearSelection={folder.clear}
      // Into whichever store this session is using. An ARRAY,
      // because that is the one shape both importers agree on: a
      // bare object throws in the solo one and an envelope is
      // pushed in whole by the multiplayer one.
      onImport={(chars) => props.library.importCharacters(JSON.stringify(chars))}
      // THE WAY OUT when a folder cannot be browsed at all. The
      // input belongs to the panel, so the folder view asks for it
      // rather than owning a second one.
      onImportFile={onImportFile}
      rooms={props.waitingRooms ?? []}
      onLoad={props.onLoadMany}
      files={props.folderFiles ?? null}
      onFiles={props.onFolderFiles}
      // STAGING IS THE OTHER DESTINATION. Importing puts characters
      // in this session's list; staging puts them in a room nobody
      // else can see, ready to walk on.
      onStage={props.onSendToRoom}
    />
  );
}

export default function CharacterLibrary(props: CharacterLibraryProps) {
  const { library, isOpen, onClose } = props;
  const panel = useLibraryPanel();
  const panelRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useCloseOnOutsidePointer(isOpen, panelRef, onClose);
  if (!isOpen) return null;
  return (
    <div
      className={clsx(styles.panel, panel.view === 'folder' && styles.panelWide)}
      ref={panelRef}
      data-testid="character-library"
    >
      {/* THE FOLDER FIRST (v8.15.0). The flat list is this session's
          characters, which die with it; the folder is the one store that
          survives, and the only one with any structure. So it is what
          the menu opens on - and the list is a click away for anyone
          who wants it, or whose browser cannot do folders. */}
      {/* THE FILE INPUT BELONGS TO THE PANEL, not to either view. It is
          hidden either way, and burying it in one tab meant that opening
          on the folder took raw JSON import off the table entirely -
          including for every test that imports a character (v8.21.1). */}
      <input
        ref={fileRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={(e) => importFromInput(e, library.importCharacters)}
      />
      <div className={styles.viewRow}>
        <ViewTabs panel={panel} />
        {props.tableAssets && <FromMyLibrary tableAssets={props.tableAssets} />}
      </div>
      {panel.view === 'folder' && (
        <FolderTab props={props} panel={panel} onImportFile={() => fileRef.current?.click()} />
      )}
      {panel.view === 'library' && <LibraryListView props={props} panel={panel} fileRef={fileRef} />}
    </div>
  );
}
